import { ItemInfrastructureDto } from '@nohara-dxdev/mt-common';
import { ApiResponse } from '@nohara-dxdev/oidc-core';
import { MutationConfig, useNotifications } from '@nohara-dxdev/react-core';
import { useScopeIds } from '@nohara-dxdev/react-oidc';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api-client';
import { ItemInfrastructureEntity } from '@/types/api';

import { getItemInfrastructuresQueryKey } from './get-item-infrastructures';

export const addOrUpdateItemInfrastructure = async (
  orgId: string,
  projectId: string,
  itemId: string,
  { infrastructureType, childItemId, ...rest }: ItemInfrastructureDto,
): Promise<ItemInfrastructureEntity> => {
  const response = await api.put<ItemInfrastructureDto, ApiResponse<ItemInfrastructureEntity>>(
    `/orgs/${orgId}/projects/${projectId}/items/${itemId}/infrastructures/${infrastructureType}/${childItemId}`,
    rest,
  );
  return response.data;
};

export interface UseAddOrUpdateItemInfrastructureOptions {
  mutationConfig?: MutationConfig<
    (params: ItemInfrastructureDto) => Promise<ItemInfrastructureEntity>
  >;
}

export const useAddOrUpdateItemInfrastructure = (
  itemId: string,
  { mutationConfig }: UseAddOrUpdateItemInfrastructureOptions = {},
) => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();
  const { orgId, projectId } = useScopeIds('org', 'project');

  const { onSuccess, onError, ...restConfig } = mutationConfig ?? {};

  return useMutation({
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({
        queryKey: getItemInfrastructuresQueryKey(orgId, projectId, itemId),
      });
      notifications.success('item-infrastructure.update.success');
      onSuccess?.(...args);
    },
    onError: (...args) => {
      notifications.error('item-infrastructure.update.error');
      onError?.(...args);
    },
    ...restConfig,
    mutationFn: (data: ItemInfrastructureDto) =>
      addOrUpdateItemInfrastructure(orgId, projectId, itemId, data),
  });
};
